import { InboxOutlined } from "@ant-design/icons";
import { Form, Input, Table, Upload } from "antd";
import React from "react";
import MetadataDefinitionStore from "../stores/MetadataDefinitionStore";
import {
    MetadataImportOverride,
    MetadataImportPreviewResult,
    MetadataImportPreviewRow,
} from "../types/Metadata";
import AppModal from "../../common/components/AppModal";

type Props = {
    store: MetadataDefinitionStore;
    isDialogVisible: boolean;
    closeDialog: () => void;
};

const FormItem = Form.Item;
const VALID_NAME_REGEX = /^[a-zA-Z0-9][a-zA-Z0-9_]*$/;

type OverrideState = Record<
    string,
    {
        name: string;
        title: string;
    }
>;

const ImportMetadataDialog: React.FC<Props> = ({
    store,
    isDialogVisible,
    closeDialog,
}) => {
    const { Dragger } = Upload;
    const [form] = Form.useForm();
    const [isLoading, setIsLoading] = React.useState(false);
    const [preview, setPreview] =
        React.useState<MetadataImportPreviewResult | null>(null);
    const [overrides, setOverrides] = React.useState<OverrideState>({});

    const conflictRows = React.useMemo(
        () =>
            preview?.rows.filter(
                (row) => row.nameConflict || row.titleConflict,
            ) ?? [],
        [preview],
    );

    const existingNames = React.useMemo(
        () =>
            new Set(store.metadataDefinitions.map((m) => m.name.toLowerCase())),
        [store.metadataDefinitions],
    );

    const existingTitles = React.useMemo(
        () =>
            new Set(
                store.metadataDefinitions
                    .filter((m) => !!m.title)
                    .map((m) => m.title),
            ),
        [store.metadataDefinitions],
    );

    const rowErrors = React.useMemo(() => {
        if (!preview) {
            return {} as Record<string, string[]>;
        }

        const nextErrors: Record<string, string[]> = {};
        const finalNames = preview.rows.map((row) => {
            const nextName = overrides[row.rowId]?.name?.trim();
            return row.nameConflict && nextName ? nextName : row.originalName;
        });
        const finalTitles = preview.rows
            .map((row) => {
                const nextTitle = overrides[row.rowId]?.title?.trim();
                return row.titleConflict && nextTitle
                    ? nextTitle
                    : row.originalTitle;
            })
            .filter(Boolean) as string[];

        const nameCounts = finalNames.reduce(
            (acc, name) => {
                const key = name.toLowerCase();
                acc[key] = (acc[key] ?? 0) + 1;
                return acc;
            },
            {} as Record<string, number>,
        );
        const titleCounts = finalTitles.reduce(
            (acc, title) => {
                acc[title] = (acc[title] ?? 0) + 1;
                return acc;
            },
            {} as Record<string, number>,
        );

        conflictRows.forEach((row) => {
            const errors: string[] = [];
            const nextName = overrides[row.rowId]?.name?.trim();
            const nextTitle = overrides[row.rowId]?.title?.trim();
            const resolvedName =
                row.nameConflict && nextName ? nextName : row.originalName;
            const resolvedTitle =
                row.titleConflict && nextTitle ? nextTitle : row.originalTitle;

            const resolvedNameKey = resolvedName.toLowerCase();
            const nextNameKey = nextName?.toLowerCase();
            const nameStillConflictsWithExisting =
                existingNames.has(resolvedNameKey);
            const nameStillConflictsInImport = nameCounts[resolvedNameKey] > 1;

            const titleStillConflictsWithExisting =
                resolvedTitle && existingTitles.has(resolvedTitle);
            const titleStillConflictsInImport =
                resolvedTitle && titleCounts[resolvedTitle] > 1;

            // Name validation based on resolved value and overrides
            if (
                nameStillConflictsWithExisting ||
                nameStillConflictsInImport ||
                !!nextName
            ) {
                if (
                    row.nameConflict &&
                    !nextName &&
                    (nameStillConflictsWithExisting ||
                        nameStillConflictsInImport)
                ) {
                    // Originally conflicting and still conflicting without override
                    errors.push("New key is required");
                } else if (nextName) {
                    if (!VALID_NAME_REGEX.test(nextName)) {
                        errors.push("New key has invalid format");
                    } else if (existingNames.has(nextNameKey!)) {
                        errors.push("New key already exists");
                    } else if (nameCounts[nextNameKey!] > 1) {
                        errors.push("New key is duplicated in this import");
                    }
                } else if (!row.nameConflict) {
                    // No override and no original conflict, but a conflict was introduced
                    if (nameStillConflictsWithExisting) {
                        errors.push("Key already exists");
                    } else if (nameStillConflictsInImport) {
                        errors.push("Key is duplicated in this import");
                    }
                }
            }

            // Title validation based on resolved value and overrides
            if (
                titleStillConflictsWithExisting ||
                titleStillConflictsInImport ||
                !!nextTitle
            ) {
                if (
                    row.titleConflict &&
                    !nextTitle &&
                    (titleStillConflictsWithExisting ||
                        titleStillConflictsInImport)
                ) {
                    // Originally conflicting and still conflicting without override
                    errors.push("New title is required");
                } else if (nextTitle) {
                    if (existingTitles.has(nextTitle)) {
                        errors.push("New title already exists");
                    } else if (titleCounts[nextTitle] > 1) {
                        errors.push("New title is duplicated in this import");
                    }
                } else if (!row.titleConflict) {
                    // No override and no original conflict, but a conflict was introduced
                    if (titleStillConflictsWithExisting) {
                        errors.push("Title already exists");
                    } else if (titleStillConflictsInImport) {
                        errors.push("Title is duplicated in this import");
                    }
                }
            }

            if (errors.length > 0) {
                nextErrors[row.rowId] = errors;
            }
        });

        return nextErrors;
    }, [conflictRows, existingNames, existingTitles, overrides, preview]);

    const hasValidationErrors = Object.keys(rowErrors).length > 0;

    const setOverrideValue = (
        rowId: string,
        field: "name" | "title",
        value: string,
    ) => {
        setOverrides((prev) => ({
            ...prev,
            [rowId]: {
                name: prev[rowId]?.name ?? "",
                title: prev[rowId]?.title ?? "",
                [field]: value,
            },
        }));
    };

    const buildOverrides = (): MetadataImportOverride[] =>
        conflictRows.map((row) => ({
            rowId: row.rowId,
            name: row.nameConflict
                ? overrides[row.rowId]?.name?.trim() || undefined
                : undefined,
            title: row.titleConflict
                ? overrides[row.rowId]?.title?.trim() || undefined
                : undefined,
        }));

    const handleAnalyze = () => {
        setIsLoading(true);
        form.validateFields()
            .then(async (values) => {
                const result = await store.previewMetadataImport(values.file);

                if (result.isOk()) {
                    setPreview(result.value);
                    setOverrides({});
                }

                setIsLoading(false);
            })
            .catch((err) => {
                setIsLoading(false);
                console.log(err);
            });
    };

    const handleImport = () => {
        setIsLoading(true);
        form.validateFields()
            .then(async (values) => {
                const result = await store.importMetadataDefinitions(
                    values.file,
                    buildOverrides(),
                );

                if (result.isOk()) {
                    form.resetFields();
                    setPreview(null);
                    setOverrides({});
                    closeDialog();
                }

                setIsLoading(false);
            })
            .catch((err) => {
                setIsLoading(false);
                console.log(err);
            });
    };

    const handleCancel = () => {
        form.resetFields();
        setPreview(null);
        setOverrides({});
        closeDialog();
    };

    const columns = [
        {
            title: "Source",
            dataIndex: "sourceFile",
            key: "sourceFile",
            render: (_: string, record: MetadataImportPreviewRow) => (
                <span>{record.sourceFile}</span>
            ),
        },
        {
            title: "Original key",
            dataIndex: "originalName",
            key: "originalName",
        },
        {
            title: "Original title",
            dataIndex: "originalTitle",
            key: "originalTitle",
            render: (value: string | undefined) => value || "-",
        },
        {
            title: "Conflicts",
            key: "conflicts",
            render: (_: string, record: MetadataImportPreviewRow) => (
                <span>
                    {[
                        record.nameConflict ? "Key" : null,
                        record.titleConflict ? "Title" : null,
                    ]
                        .filter(Boolean)
                        .join(", ")}
                </span>
            ),
        },
        {
            title: "New key",
            key: "newName",
            render: (_: string, record: MetadataImportPreviewRow) =>
                record.nameConflict ? (
                    <Input
                        value={overrides[record.rowId]?.name ?? ""}
                        onChange={(event) =>
                            setOverrideValue(
                                record.rowId,
                                "name",
                                event.target.value,
                            )
                        }
                    />
                ) : (
                    "-"
                ),
        },
        {
            title: "New title",
            key: "newTitle",
            render: (_: string, record: MetadataImportPreviewRow) =>
                record.titleConflict ? (
                    <Input
                        value={overrides[record.rowId]?.title ?? ""}
                        onChange={(event) =>
                            setOverrideValue(
                                record.rowId,
                                "title",
                                event.target.value,
                            )
                        }
                    />
                ) : (
                    "-"
                ),
        },
        {
            title: "Validation",
            key: "validation",
            render: (_: string, record: MetadataImportPreviewRow) => (
                <span style={{ color: "#c0392b" }}>
                    {rowErrors[record.rowId]?.join(". ") ?? ""}
                </span>
            ),
        },
    ];

    return (
        <AppModal
            centered
            width={1100}
            title="Import metadata"
            destroyOnClose
            closable={false}
            maskClosable={false}
            open={isDialogVisible}
            className="alpha-modal"
            cancelButtonTitle="Cancel"
            onCancelClick={handleCancel}
            submitButtonTitle={preview ? "Import metadata" : "Analyze import"}
            onSubmitClick={preview ? handleImport : handleAnalyze}
            submitButtonProps={{
                form: "import-metadata-form",
                loading: isLoading,
                disabled:
                    !!preview && preview.hasConflicts && hasValidationErrors,
            }}
        >
            <Form
                className="inputs-container"
                style={{ marginTop: 10 }}
                id="import-metadata-form"
                layout="vertical"
                form={form}
            >
                <FormItem
                    name="file"
                    valuePropName="file"
                    colon={false}
                    className="dialog-field"
                    label={
                        <span className="dialog-field-label">
                            Metadata file
                        </span>
                    }
                    rules={[
                        {
                            required: true,
                            message: "Metadata file is required",
                        },
                    ]}
                    getValueFromEvent={(e) => {
                        if (!e.file || !e.fileList || !e.fileList.length) {
                            setPreview(null);
                            setOverrides({});
                            return;
                        }

                        setPreview(null);
                        setOverrides({});
                        return e.file;
                    }}
                >
                    <Dragger
                        name="file"
                        beforeUpload={() => false}
                        maxCount={1}
                        accept=".metadata,.zip"
                    >
                        <p className="ant-upload-drag-icon">
                            <InboxOutlined />
                        </p>
                        <p className="ant-upload-text">
                            Click or drag a .metadata or .zip file to this area
                            to upload
                        </p>
                    </Dragger>
                </FormItem>
                {preview && (
                    <div className="dialog-field">
                        <div style={{ marginBottom: 12 }}>
                            Analyzed {preview.totalCount} metadata row(s).
                            {preview.hasConflicts
                                ? ` ${preview.conflictCount} row(s) need updated keys or titles before import.`
                                : " No duplicate names or titles found. The file is ready to import."}
                        </div>
                        {preview.hasConflicts && (
                            <Table
                                rowKey={(row: MetadataImportPreviewRow) =>
                                    row.rowId
                                }
                                dataSource={conflictRows}
                                columns={columns}
                                pagination={false}
                                scroll={{ x: 1000, y: 260 }}
                            />
                        )}
                    </div>
                )}
            </Form>
        </AppModal>
    );
};

export default ImportMetadataDialog;
